import { DEMO_COMPANION_BOOKING_MESSAGES } from '../data/companionsData';

export type SupportedLanguage = 'en' | 'bn' | 'hi';

export interface LanguageMetadata {
  code: SupportedLanguage;
  label: string;
  nativeLabel: string;
  shortLabel: string;
}

export const SUPPORTED_LANGUAGES: LanguageMetadata[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', shortLabel: 'EN' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা', shortLabel: 'বাং' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', shortLabel: 'हिं' },
];

export const TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'India’s Trusted Verified Social & Event Companionship Platform',
    'common.language': 'Language',
    'common.selectLanguage': 'Select Language',
    'common.continue': 'Continue',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.save': 'Save Changes',
    'common.saved': 'Saved',
    'common.loading': 'Loading...',
    'common.verified': 'Verified',
    'common.demoProfile': 'Showcase Profile',
    'common.demoBadge': 'Showcase',
    'demo.badge': 'Showcase',
    'common.perHour': '/hr',
    'common.search': 'Search',
    'common.back': 'Back',

    // Navigation
    'nav.home': 'Home',
    'nav.findCompanions': 'Find Companions',
    'nav.howItWorks': 'How It Works',
    'nav.safety': 'Safety',
    'nav.membership': 'Membership',
    'nav.becomeCompanion': 'Become a Companion',
    'nav.login': 'Login',
    'nav.register': 'Sign Up',
    'nav.loginRegister': 'Login / Register',
    'nav.dashboard': 'Dashboard',
    'nav.myBookings': 'My Bookings',
    'nav.wishlist': 'Wishlist',
    'nav.messages': 'Messages',
    'nav.earnings': 'Earnings & Payouts',
    'nav.kycVerification': 'KYC & Bank Verification',
    'nav.profile': 'My Profile',
    'nav.settings': 'Settings',
    'nav.logout': 'Log Out',
    'nav.switchToClient': 'Client Mode',
    'nav.switchToCompanion': 'Companion Mode',

    // Hero & Discovery
    'hero.badge': '100% ID-Verified Safe Public Companionship',
    'hero.title': 'Find Your Perfect Companion for Every Occasion',
    'hero.subtitle': 'Book verified companions for dining, travel, cultural walks, corporate events, and chauffeur-assisted tours across India.',
    'hero.exploreBtn': 'Explore Companions',
    'hero.becomeCompanionBtn': 'Join as Companion',
    'hero.findMale': 'Find Male Companion',
    'hero.findFemale': 'Find Female Companion',
    'discovery.title': 'Explore Companions',
    'discovery.subtitle': 'Browse verified companions and showcase profiles across Indian cities',
    'discovery.searchPlaceholder': 'Search by city, locality, or companion name...',
    'discovery.nearbyTitle': 'Companions Near You',
    'discovery.bookNow': 'Book Now',
    'discovery.viewProfile': 'View Profile',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'Showcase Profile: You can view photos and details, but bookings are enabled only for verified real Companion accounts.',

    // Authentication
    'auth.clientLoginTitle': 'Client Sign In',
    'auth.companionLoginTitle': 'Companion Sign In',
    'auth.mobileOtpTab': 'Mobile + OTP',
    'auth.emailPasswordTab': 'Email & Password',
    'auth.sendOtp': 'Send OTP via SMS',
    'auth.verifyAndLogin': 'Verify OTP & Sign In',
    'auth.createAccount': 'Create New Account',
    'auth.existingAccountNotice': 'Only registered accounts can sign in. New users must complete registration first.',
    'auth.adminPortalTitle': 'PairUpToday Admin Login',
    'auth.admin2faRequired': 'Mandatory 2FA Verification Required',

    // First-Open Permissions
    'permissions.title': 'Permissions Required for Safe Experience',
    'permissions.subtitle': 'PairUp Today uses location for nearby companion discovery and camera for identity verification.',
    'permissions.locationTitle': 'Location Access (Nearby Companions)',
    'permissions.locationDesc': 'Find verified companions and safe public meeting venues in your city.',
    'permissions.cameraTitle': 'Camera & Microphone Access',
    'permissions.cameraDesc': 'Required for live KYC selfie verification and profile photo upload.',
    'permissions.notificationTitle': 'Booking & OTP Notifications',
    'permissions.notificationDesc': 'Receive instant booking updates, meetup check-in alerts, and security codes.',
    'permissions.allowContinue': 'Allow & Continue',

    // Booking Modal
    'booking.title': 'Confirm Companion Booking',
    'booking.selectDate': 'Meeting Date',
    'booking.selectTime': 'Start Time',
    'booking.duration': 'Duration',
    'booking.meetingLocation': 'Public Meeting Venue (India)',
    'booking.payAndConfirm': 'Pay & Confirm Booking via Razorpay',
    'booking.escrowNotice': '100% Escrow Protected: Companion payout is released only after in-person meetup OTP verification.',

    // Membership
    'membership.title': 'Companion Membership Plans',
    'membership.subtitle': 'Activate your verified Companion profile with server-verified Razorpay checkout.',
    'membership.starter': '1 Month Starter Plan (₹449)',
    'membership.professional': '3 Months Professional Plan (₹1,099)',
    'membership.elite': '1 Year Elite Plan (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'Identity & Bank Verification',
    'kyc.subtitle': 'Complete Government ID KYC and Pennyless Bank Verification to receive payouts.',
    'kyc.bankTitle': 'Companion Bank Verification (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'Verify Bank Account',

    // Settings
    'settings.languageTitle': 'Language Preference (ভাষা / भाषा)',
    'settings.languageDesc': 'Select your preferred language for Web, Android, and iOS. Changes apply immediately.',
  },

  bn: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'ভারতের বিশ্বস্ত ও ভেরিফাইড সোশ্যাল এবং ইভেন্ট কম্প্যানিয়নশিপ প্ল্যাটফর্ম',
    'common.language': 'ভাষা',
    'common.selectLanguage': 'ভাষা নির্বাচন করুন',
    'common.continue': 'এগিয়ে যান',
    'common.cancel': 'বাতিল করুন',
    'common.close': 'বন্ধ করুন',
    'common.save': 'পরিবর্তন সংরক্ষণ করুন',
    'common.saved': 'সংরক্ষিত হয়েছে',
    'common.loading': 'লোড হচ্ছে...',
    'common.verified': 'ভেরিফাইড',
    'common.demoProfile': 'প্রদর্শনী প্রোফাইল',
    'common.demoBadge': 'প্রদর্শনী',
    'demo.badge': 'প্রদর্শনী',
    'common.perHour': '/ঘণ্টা',
    'common.search': 'অনুসন্ধান',
    'common.back': 'ফিরে যান',

    // Navigation
    'nav.home': 'হোম',
    'nav.findCompanions': 'Companion খুঁজুন',
    'nav.howItWorks': 'কীভাবে কাজ করে',
    'nav.safety': 'নিরাপত্তা',
    'nav.membership': 'মেম্বারশিপ',
    'nav.becomeCompanion': 'Companion হিসেবে যোগ দিন',
    'nav.login': 'লগইন',
    'nav.register': 'রেজিস্টার',
    'nav.loginRegister': 'লগইন / রেজিস্টার',
    'nav.dashboard': 'ড্যাশবোর্ড',
    'nav.myBookings': 'আমার বুকিংসমূহ',
    'nav.wishlist': 'উইশলিস্ট',
    'nav.messages': 'মেসেজ',
    'nav.earnings': 'আয় ও পেআউট',
    'nav.kycVerification': 'KYC ও ব্যাংক ভেরিফিকেশন',
    'nav.profile': 'আমার প্রোফাইল',
    'nav.settings': 'সেটিংস',
    'nav.logout': 'লগ আউট',
    'nav.switchToClient': 'Client মোড',
    'nav.switchToCompanion': 'Companion মোড',

    // Hero & Discovery
    'hero.badge': '১০০% আইডি-ভেরিফাইড নিরাপদ পাবলিক কম্প্যানিয়নশিপ',
    'hero.title': 'প্রতিটি বিশেষ মুহূর্তের জন্য আপনার উপযুক্ত Companion খুঁজুন',
    'hero.subtitle': 'ডাইনিং, ভ্রমণ, কালচারাল ট্যুর, কর্পোরেট ইভেন্ট এবং ড্রাইভার-সহ ট্যুরের জন্য ভেরিফাইড Companion বুক করুন।',
    'hero.exploreBtn': 'Companion খুঁজুন',
    'hero.becomeCompanionBtn': 'Companion হিসেবে যুক্ত হোন',
    'hero.findMale': 'পুরুষ সঙ্গী খুঁজুন (Find Male Companion)',
    'hero.findFemale': 'নারী সঙ্গী খুঁজুন (Find Female Companion)',
    'discovery.title': 'Companion প্রোফাইলসমূহ দেখুন',
    'discovery.subtitle': 'ভারতের বিভিন্ন শহরে ভেরিফাইড Companion এবং প্রদর্শনী প্রোফাইল ব্রাউজ করুন',
    'discovery.searchPlaceholder': 'শহর, এলাকা বা নাম দিয়ে খুঁজুন...',
    'discovery.nearbyTitle': 'আপনার কাছাকাছি Companion',
    'discovery.bookNow': 'বুক করুন',
    'discovery.viewProfile': 'প্রোফাইল দেখুন',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'প্রদর্শনী প্রোফাইল: আপনি ছবি ও তথ্য দেখতে পারবেন, কিন্তু শুধুমাত্র ভেরিফাইড আসল Companion প্রোফাইল বুক করা যাবে।',

    // Authentication
    'auth.clientLoginTitle': 'Client লগইন',
    'auth.companionLoginTitle': 'Companion লগইন',
    'auth.mobileOtpTab': 'মোবাইল + OTP',
    'auth.emailPasswordTab': 'ইমেইল ও পাসওয়ার্ড',
    'auth.sendOtp': 'SMS-এ OTP পাঠান',
    'auth.verifyAndLogin': 'OTP যাচাই করে লগইন করুন',
    'auth.createAccount': 'নতুন অ্যাকাউন্ট খুলুন',
    'auth.existingAccountNotice': 'শুধুমাত্র রেজিস্টার্ড অ্যাকাউন্ট লগইন করতে পারবে। নতুন ব্যবহারকারীদের আগে অ্যাকাউন্ট রেজিস্টার করতে হবে।',
    'auth.adminPortalTitle': 'PairUpToday অ্যাডমিন লগইন',
    'auth.admin2faRequired': 'বাধ্যতামূলক 2FA যাচাইকরণ প্রয়োজন',

    // First-Open Permissions
    'permissions.title': 'নিরাপদ ব্যবহারের জন্য অনুমতি প্রয়োজন',
    'permissions.subtitle': 'কাছাকাছি Companion খোঁজার জন্য লোকেশন এবং KYC যাচাইয়ের জন্য ক্যামেরা অনুমতি প্রয়োজন।',
    'permissions.locationTitle': 'লোকেশন অ্যাক্সেস (কাছাকাছি Companion)',
    'permissions.locationDesc': 'আপনার শহরে ভেরিফাইড Companion এবং নিরাপদ পাবলিক মিটিং স্থান খুঁজতে সাহায্য করে।',
    'permissions.cameraTitle': 'ক্যামেরা ও মাইক্রোফোন অ্যাক্সেস',
    'permissions.cameraDesc': 'লাইভ KYC সেলফি ভেরিফিকেশন এবং প্রোফাইল ছবি আপলোডের জন্য প্রয়োজন।',
    'permissions.notificationTitle': 'বুকিং ও OTP নোটিফিকেশন',
    'permissions.notificationDesc': 'বুকিং আপডেট, মিটআপ চেক-ইন এবং সিকিউরিটি OTP সাথে সাথে পান।',
    'permissions.allowContinue': 'অনুমতি দিন ও এগিয়ে যান',

    // Booking Modal
    'booking.title': 'Companion বুকিং নিশ্চিত করুন',
    'booking.selectDate': 'সাক্ষাতের তারিখ',
    'booking.selectTime': 'শুরুর সময়',
    'booking.duration': 'সময়কাল',
    'booking.meetingLocation': 'পাবলিক মিটিং স্থান (ভারত)',
    'booking.payAndConfirm': 'Razorpay দিয়ে পেমেন্ট ও বুকিং নিশ্চিত করুন',
    'booking.escrowNotice': '১০০% এসক্রো সুরক্ষিত: সরাসরি সাক্ষাতের পর OTP যাচাই হলে তবেই Companion পেআউট রিলিজ হয়।',

    // Membership
    'membership.title': 'Companion মেম্বারশিপ প্ল্যান',
    'membership.subtitle': 'সার্ভার-ভেরিফাইড Razorpay পেমেন্টের মাধ্যমে আপনার Companion প্রোফাইল সক্রিয় করুন।',
    'membership.starter': '১ মাসের স্টার্টার প্ল্যান (₹449)',
    'membership.professional': '৩ মাসের প্রফেশনাল প্ল্যান (₹1,099)',
    'membership.elite': '১ বছরের এলিট প্ল্যান (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'পরিচয়পত্র (KYC) ও ব্যাংক ভেরিফিকেশন',
    'kyc.subtitle': 'পেআউট পেতে সরকারি আইডি KYC এবং পেনিলেস ব্যাংক ভেরিফিকেশন সম্পন্ন করুন।',
    'kyc.bankTitle': 'Companion ব্যাংক ভেরিফিকেশন (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'ব্যাংক অ্যাকাউন্ট যাচাই করুন',

    // Settings
    'settings.languageTitle': 'ভাষা পছন্দ (Language Preference)',
    'settings.languageDesc': 'Web, Android এবং iOS-এর জন্য আপনার পছন্দের ভাষা নির্বাচন করুন। পরিবর্তন সাথে সাথে কার্যকর হবে।',
  },

  hi: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'भारत का विश्वसनीय और सत्यापित सोशल एवं इवेंट कम्पैनियनशिप प्लेटफ़ॉर्म',
    'common.language': 'भाषा',
    'common.selectLanguage': 'भाषा चुनें',
    'common.continue': 'आगे बढ़ें',
    'common.cancel': 'रद्द करें',
    'common.close': 'बंद करें',
    'common.save': 'परिवर्तन सहेजें',
    'common.saved': 'सहेजा गया',
    'common.loading': 'लोड हो रहा है...',
    'common.verified': 'सत्यापित',
    'common.demoProfile': 'प्रदर्शनी प्रोफ़ाइल',
    'common.demoBadge': 'प्रदर्शनी',
    'demo.badge': 'प्रदर्शनी',
    'common.perHour': '/घंटा',
    'common.search': 'खोजें',
    'common.back': 'वापस',

    // Navigation
    'nav.home': 'होम',
    'nav.findCompanions': 'Companion खोजें',
    'nav.howItWorks': 'यह कैसे काम करता है',
    'nav.safety': 'सुरक्षा',
    'nav.membership': 'सदस्यता (Membership)',
    'nav.becomeCompanion': 'Companion बनें',
    'nav.login': 'लॉगिन',
    'nav.register': 'साइन अप',
    'nav.loginRegister': 'लॉगिन / साइन अप',
    'nav.dashboard': 'डैशबोर्ड',
    'nav.myBookings': 'मेरी बुकिंग',
    'nav.wishlist': 'विशलिस्ट',
    'nav.messages': 'संदेश',
    'nav.earnings': 'कमाई और भुगतान',
    'nav.kycVerification': 'KYC और बैंक सत्यापन',
    'nav.profile': 'मेरी प्रोफ़ाइल',
    'nav.settings': 'सेटिंग्स',
    'nav.logout': 'लॉग आउट',
    'nav.switchToClient': 'Client मोड',
    'nav.switchToCompanion': 'Companion मोड',

    // Hero & Discovery
    'hero.badge': '100% आईडी-सत्यापित सुरक्षित पब्लिक कम्पैनियनशिप',
    'hero.title': 'हर अवसर के लिए अपना उपयुक्त Companion खोजें',
    'hero.subtitle': 'डाइनिंग, यात्रा, सांस्कृतिक भ्रमण, कॉर्पोरेट इवेंट और ड्राइवर-सहायता प्राप्त टूर के लिए सत्यापित Companion बुक करें।',
    'hero.exploreBtn': 'Companion खोजें',
    'hero.becomeCompanionBtn': 'Companion के रूप में जुड़ें',
    'hero.findMale': 'पुरुष साथी खोजें (Find Male Companion)',
    'hero.findFemale': 'महिला साथी खोजें (Find Female Companion)',
    'discovery.title': 'Companion प्रोफ़ाइल देखें',
    'discovery.subtitle': 'भारतीय शहरों में सत्यापित Companion और शोकेस प्रोफ़ाइल ब्राउज़ करें',
    'discovery.searchPlaceholder': 'शहर, क्षेत्र या Companion के नाम से खोजें...',
    'discovery.nearbyTitle': 'आपके आस-पास के Companion',
    'discovery.bookNow': 'अभी बुक करें',
    'discovery.viewProfile': 'प्रोफ़ाइल देखें',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'प्रदर्शनी प्रोफ़ाइल: आप फ़ोटो और विवरण देख सकते हैं, लेकिन केवल सत्यापित वास्तविक Companion प्रोफ़ाइल ही बुक की जा सकती हैं।',

    // Authentication
    'auth.clientLoginTitle': 'Client लॉगिन',
    'auth.companionLoginTitle': 'Companion लॉगिन',
    'auth.mobileOtpTab': 'मोबाइल + OTP',
    'auth.emailPasswordTab': 'ईमेल और पासवर्ड',
    'auth.sendOtp': 'SMS द्वारा OTP भेजें',
    'auth.verifyAndLogin': 'OTP सत्यापित करें और लॉगिन करें',
    'auth.createAccount': 'नया खाता बनाएँ',
    'auth.existingAccountNotice': 'केवल पंजीकृत खाते ही लॉगिन कर सकते हैं। नए उपयोगकर्ताओं को पहले पंजीकरण पूरा करना होगा।',
    'auth.adminPortalTitle': 'PairUpToday एडमिन लॉगिन',
    'auth.admin2faRequired': 'अनिवार्य 2FA सत्यापन आवश्यक है',

    // First-Open Permissions
    'permissions.title': 'सुरक्षित अनुभव के लिए अनुमतियाँ आवश्यक हैं',
    'permissions.subtitle': 'आस-पास के Companion खोजने के लिए लोकेशन और पहचान सत्यापन के लिए कैमरा अनुमति आवश्यक है।',
    'permissions.locationTitle': 'लोकेशन एक्सेस (आस-पास के Companion)',
    'permissions.locationDesc': 'अपने शहर में सत्यापित Companion और सुरक्षित सार्वजनिक मीटिंग स्थान खोजें।',
    'permissions.cameraTitle': 'कैमरा और माइक्रोफ़ोन एक्सेस',
    'permissions.cameraDesc': 'लाइव KYC सेल्फ़ी सत्यापन और प्रोफ़ाइल फ़ोटो अपलोड के लिए आवश्यक।',
    'permissions.notificationTitle': 'बुकिंग और OTP सूचनाएँ',
    'permissions.notificationDesc': 'तुरंत बुकिंग अपडेट, मीटअप चेक-इन अलर्ट और सुरक्षा कोड प्राप्त करें।',
    'permissions.allowContinue': 'अनुमति दें और आगे बढ़ें',

    // Booking Modal
    'booking.title': 'Companion बुकिंग की पुष्टि करें',
    'booking.selectDate': 'मुलाकात की तारीख',
    'booking.selectTime': 'शुरू होने का समय',
    'booking.duration': 'अवधि',
    'booking.meetingLocation': 'सार्वजनिक मीटिंग स्थान (भारत)',
    'booking.payAndConfirm': 'Razorpay से भुगतान और बुकिंग की पुष्टि करें',
    'booking.escrowNotice': '100% एस्क्रो सुरक्षित: व्यक्तिगत मुलाकात के बाद OTP सत्यापन होने पर ही Companion भुगतान जारी किया जाता है।',

    // Membership
    'membership.title': 'Companion सदस्यता प्लान',
    'membership.subtitle': 'सर्वर-सत्यापित Razorpay भुगतान के साथ अपनी Companion प्रोफ़ाइल सक्रिय करें।',
    'membership.starter': '1 माह स्टार्टर प्लान (₹449)',
    'membership.professional': '3 माह प्रोफ़ेशनल प्लान (₹1,099)',
    'membership.elite': '1 वर्ष एलीट प्लान (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'पहचान (KYC) और बैंक सत्यापन',
    'kyc.subtitle': 'भुगतान प्राप्त करने के लिए सरकारी आईडी KYC और पेनीलेस बैंक सत्यापन पूरा करें।',
    'kyc.bankTitle': 'Companion बैंक सत्यापन (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'बैंक खाता सत्यापित करें',

    // Settings
    'settings.languageTitle': 'भाषा प्राथमिकता (Language Preference)',
    'settings.languageDesc': 'Web, Android और iOS के लिए अपनी पसंदीदा भाषा चुनें। बदलाव तुरंत लागू होंगे।',
  },
};

export function translateKey(lang: SupportedLanguage, key: string, fallback?: string): string {
  const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
  return dict[key] || TRANSLATIONS.en[key] || fallback || key;
}
